import PropTypes from "prop-types";
import { useState } from "react";
import { sentenceCase } from "change-case";
// material
import {
  Box,
  Grid,
  Card,
  Button,
  Typography,
  Stack,
  DialogActions,
  TextField,
  InputAdornment,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbar } from "notistack";
import { LoadingButton } from "@mui/lab";
import { fCurrency } from "../../../utils/formatNumber";
import { fDate } from "../../../utils/formatTime";
import { METHOD_OPTIONS } from "../../../utils/apis";
import useAuth from "../../../hooks/useAuth";
import { ShowError } from "../../../utils/loader";

// ----------------------------------------------------------------------

SingleCard.propTypes = {
  payment: PropTypes.object,
  handleRemove: PropTypes.func,
};

function SingleCard({ payment, handleRemove }) {
  const { amount, info, method, date } = payment;
  return (
    <Card
      variant="outlined"
      sx={{
        border: "0.4px solid",
        borderColor: "background.border",
        boxShadow:
          "4px 2px 124px rgba(46, 53, 58, 0.02), 0px 4px 124px rgba(46, 41, 78, 0.02)",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        p: 3,
      }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0, pl: 2, pr: 1 }}>
        <Typography variant="subtitle2" noWrap>
          {fCurrency(amount)}
        </Typography>
        <Typography variant="subtitle2" noWrap>
          Method: {sentenceCase(method)}
        </Typography>
        <Typography variant="subtitle2" noWrap>
          On {date ? fDate(date) : ""}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {info}
          </Typography>
        </Box>
      </Box>
      <IconButton color="error" onClick={handleRemove}>
        <DeleteIcon />
      </IconButton>
    </Card>
  );
}

AddNew.propTypes = {
  open: PropTypes.bool,
  loading: PropTypes.bool,
  handleAdd: PropTypes.func,
  handleClose: PropTypes.func,
  handleChange: PropTypes.func,
  values: PropTypes.object,
};
function AddNew({
  open,
  handleClose,
  handleAdd,
  loading,
  values,
  handleChange,
}) {
  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose}>
      <DialogTitle>Add a new payment</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={4} sx={{ width: 1, p: 2, px: 0.5 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ width: 1 }}
          >
            <TextField
              size="small"
              sx={{ maxWidth: { md: 300 }, width: 300 }}
              select
              name="method"
              value={values.method}
              label="Method"
              InputLabelProps={{ shrink: true }}
              onChange={handleChange}
              SelectProps={{
                native: false,
                sx: { textTransform: "capitalize" },
              }}
            >
              {METHOD_OPTIONS.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  sx={{
                    mx: 1,
                    my: 0.5,
                    borderRadius: 0.75,
                    typography: "body2",
                    textTransform: "capitalize",
                  }}
                >
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="number"
              name="amount"
              value={values.amount}
              label="Amount Paid"
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">&#8358;</InputAdornment>
                ),
              }}
            />
          </Stack>
          <TextField
            size="small"
            type="date"
            name="date"
            value={values.date}
            label="Date Paid"
            InputLabelProps={{
              shrink: true,
            }}
            onChange={handleChange}
          />
          <TextField
            size="small"
            type="text"
            fullWidth
            multiline
            name="info"
            value={values.info}
            label="Other info(e.g Account Money was paid into etc)"
            InputLabelProps={{
              shrink: true,
            }}
            rows="4"
            onChange={handleChange}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Close
        </Button>
        <LoadingButton
          onClick={handleAdd}
          variant="contained"
          loading={loading}
        >
          Add
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

DeleteModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleDelete: PropTypes.func,
  loading: PropTypes.bool,
};
function DeleteModal({ open, handleClose, handleDelete, loading }) {
  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle sx={{ color: "error.main" }}>
        Please confirm you want to delete this payment?
      </DialogTitle>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          onClick={handleDelete}
          variant="contained"
          loading={loading}
          color="error"
        >
          Delete
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

PaymentHistory.propTypes = {
  payments: PropTypes.string.isRequired,
  rid: PropTypes.string.isRequired,
  fetch: PropTypes.func.isRequired,
};

export default function PaymentHistory({ payments, rid, fetch }) {
  const actualPayments = payments !== "" ? JSON.parse(payments) : [];
  const { addDebtPayment, removeDebtPayment } = useAuth();
  const [open, setOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toDelete, setToDelete] = useState("");
  const [values, setValues] = useState({
    method: "",
    date: "",
    amount: "",
    info: "",
  });
  const { enqueueSnackbar } = useSnackbar();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };
  const handleAdd = async () => {
    try {
      if (values.amount && values.method) {
        setLoading(true);
        await addDebtPayment(values, rid);
        setValues({ method: "", date: "", amount: "", info: "" });
        fetch();
      } else {
        enqueueSnackbar(
          `Please enter atleast an amount and payment method to proceed.`,
          { variant: "error" }
        );
      }
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
    setLoading(false);
  };
  const handleDelete = async () => {
    try {
      if (toDelete !== "") {
        setIsSubmitting(true);
        await removeDebtPayment(toDelete, rid);
        setToDelete("");
        fetch();
      } else {
        enqueueSnackbar(`Something went wrong! Please try again later.`, {
          variant: "error",
        });
      }
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
    setIsSubmitting(false);
  };
  const handleRemove = (index) => {
    setToDelete(index);
    setOpenDelete(true);
  };
  return (
    <Box sx={{ mt: 5 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h4" sx={{ mb: 3 }}>
          Payments
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => setOpen(true)}
        >
          Add
        </Button>
      </Stack>
      {actualPayments.length > 0 ? (
        <Grid container spacing={3}>
          {actualPayments.map((payment, index) => (
            <Grid key={index} item xs={12} md={4}>
              <SingleCard
                payment={payment}
                handleRemove={() => handleRemove(index)}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <ShowError
          message={
            new Error(
              "This debtor does not have any payment attached at the moment!"
            )
          }
        />
      )}
      <AddNew
        open={open}
        handleClose={() => setOpen(false)}
        handleAdd={handleAdd}
        loading={loading}
        values={values}
        handleChange={handleChange}
      />
      <DeleteModal
        open={openDelete}
        handleClose={() => setOpenDelete(false)}
        handleDelete={handleDelete}
        loading={isSubmitting}
      />
    </Box>
  );
}
